<template>
  <section class="notification-center">
    <template v-if="isDetail">
      <header class="notification-center__page-heading notification-center__detail-heading">
        <button type="button" @click="$emit('back')">
          <i class="el-icon-arrow-left" aria-hidden="true" />
          返回通知中心
        </button>
      </header>

      <article class="notification-detail up-card">
        <div v-if="detailLoading" class="notification-center__state">
          <i class="el-icon-loading" aria-hidden="true" />
          正在读取通知详情…
        </div>
        <div v-else-if="detailError" class="notification-center__state is-error">
          <i class="el-icon-warning-outline" aria-hidden="true" />
          <span>{{ detailError }}</span>
          <button type="button" @click="loadDetail">重新加载</button>
        </div>
        <template v-else-if="detail">
          <header>
            <span>{{ typeLabel(detail.type) }}</span>
            <h1 v-raw-text="detail.title" />
            <time v-raw-text="detail.sendTime || '--'" />
          </header>
          <iframe
            v-if="detailExternalUrl"
            class="notification-detail__iframe"
            :src="detailExternalUrl"
            title="通知详情"
          />
          <div v-else v-raw-html="detailHtml" class="notification-detail__content" />
        </template>
      </article>
    </template>

    <template v-else>
      <header class="notification-center__page-heading">
        <div>
          <h1>通知中心</h1>
          <p>查看公告、交易动态及系统消息</p>
        </div>
        <button
          class="notification-center__read-all"
          type="button"
          :disabled="readAllLoading || totalUnread === 0"
          @click="readAll"
        >
          <i v-if="readAllLoading" class="el-icon-loading" aria-hidden="true" />
          {{ readAllLoading ? '处理中…' : '全部已读' }}
        </button>
      </header>

      <div class="notification-center__layout">
        <aside class="notification-center__categories up-card" aria-label="通知分类">
          <button
            v-for="type in types"
            :key="type.id"
            type="button"
            :class="{ active: currentType === type.id }"
            @click="selectType(type.id)"
          >
            <span class="notification-center__category-icon" :class="`type-${type.id}`">
              <i :class="typeIcon(type.id)" aria-hidden="true" />
            </span>
            <strong>{{ type.label }}</strong>
            <i v-if="typeCounts[type.id]" class="notification-center__category-count">
              {{ countText(typeCounts[type.id]) }}
            </i>
            <i class="el-icon-arrow-right notification-center__category-arrow" aria-hidden="true" />
          </button>
        </aside>

        <article class="notification-center__list up-card">
          <header>
            <div>
              <h2>{{ currentTypeLabel }}</h2>
              <span v-if="typeCountsLoading">正在同步未读数量…</span>
              <span v-else>{{ typeCounts[currentType] || 0 }} 条未读</span>
            </div>
          </header>

          <div v-if="listLoading" class="notification-center__state">
            <i class="el-icon-loading" aria-hidden="true" />
            正在读取通知…
          </div>
          <div v-else-if="listError" class="notification-center__state is-error">
            <i class="el-icon-warning-outline" aria-hidden="true" />
            <span>{{ listError }}</span>
            <button type="button" @click="loadList">重新加载</button>
          </div>
          <div v-else-if="!rows.length" class="notification-center__state">
            <i class="el-icon-bell" aria-hidden="true" />
            暂无{{ currentTypeLabel }}
          </div>
          <div v-else class="notification-center__rows">
            <button
              v-for="item in rows"
              :key="item.id"
              class="notification-center__row"
              type="button"
              @click="$emit('open', item)"
            >
              <span class="notification-center__row-dot" :class="{ unread: item.unread }" />
              <span class="notification-center__row-copy">
                <strong v-raw-text="item.title" />
                <small v-raw-text="item.preview || $t('点击查看通知详情')" />
              </span>
              <time v-raw-text="item.sendTime || '--'" />
              <i class="el-icon-arrow-right" aria-hidden="true" />
            </button>
          </div>

          <footer v-if="total > pageSize">
            <span>共 {{ total }} 条消息</span>
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="page"
              :page-size="pageSize"
              :pager-count="paginationPagerCount"
              :total="total"
              @current-change="changePage"
            />
          </footer>
        </article>
      </div>
    </template>
  </section>
</template>

<script>
import {
  NOTIFICATION_TYPES,
  loadNotificationDetail,
  loadNotificationPage,
  loadNotificationTypeCounts,
  markAllNotificationsRead,
  safeNotificationHtml
} from '@/api/notifications'
import { rawHtml, rawText } from '@/directives/raw-content'

export default {
  name: 'NotificationsPanel',
  directives: { rawHtml, rawText },
  props: {
    messageId: { type: [String, Number], default: '' }
  },
  data() {
    const queryType = Number(this.$route.query.type)
    return {
      types: NOTIFICATION_TYPES,
      currentType: NOTIFICATION_TYPES.some(item => item.id === queryType) ? queryType : 7,
      rows: [],
      page: 1,
      pageSize: 10,
      paginationPagerCount: 7,
      total: 0,
      listLoading: false,
      listError: '',
      typeCounts: {},
      typeCountsLoading: false,
      readAllLoading: false,
      detail: null,
      detailLoading: false,
      detailError: ''
    }
  },
  computed: {
    isDetail() {
      return Boolean(String(this.messageId || '').trim())
    },
    currentTypeLabel() {
      return this.typeLabel(this.currentType)
    },
    totalUnread() {
      return Object.values(this.typeCounts).reduce((sum, value) => sum + Number(value || 0), 0)
    },
    detailHtml() {
      return safeNotificationHtml(this.detail && this.detail.content)
    },
    detailExternalUrl() {
      const content = String(this.detail && this.detail.content || '')
      if (!content.includes('messageContent')) return ''
      const text = content.replace(/<[^>]+>/g, '').trim()
      return /^https?:\/\//i.test(text) ? text : ''
    }
  },
  watch: {
    messageId: {
      immediate: true,
      handler(value) {
        if (String(value || '').trim()) {
          this.loadDetail()
        } else {
          this.loadCounts()
          this.loadList()
        }
      }
    }
  },
  mounted() {
    this.paginationMediaQuery = window.matchMedia('(max-width: 760px)')
    this.syncPaginationPagerCount(this.paginationMediaQuery)
    if (this.paginationMediaQuery.addEventListener) {
      this.paginationMediaQuery.addEventListener('change', this.syncPaginationPagerCount)
    } else {
      this.paginationMediaQuery.addListener(this.syncPaginationPagerCount)
    }
  },
  beforeDestroy() {
    if (!this.paginationMediaQuery) return
    if (this.paginationMediaQuery.removeEventListener) {
      this.paginationMediaQuery.removeEventListener('change', this.syncPaginationPagerCount)
    } else {
      this.paginationMediaQuery.removeListener(this.syncPaginationPagerCount)
    }
  },
  methods: {
    syncPaginationPagerCount(mediaQuery) {
      this.paginationPagerCount = mediaQuery && mediaQuery.matches ? 5 : 7
    },
    typeLabel(type) {
      const current = this.types.find(item => item.id === Number(type))
      return current ? current.label : this.$t('通知')
    },
    typeIcon(type) {
      return {
        7: 'el-icon-bell',
        2: 'el-icon-s-data',
        6: 'el-icon-document',
        1: 'el-icon-setting'
      }[Number(type)] || 'el-icon-message'
    },
    countText(count) {
      return Number(count) > 99 ? '99+' : String(count)
    },
    async loadCounts() {
      this.typeCountsLoading = true
      try {
        this.typeCounts = await loadNotificationTypeCounts()
      } catch (error) {
        this.$emit('toast', { message: error.message || this.$t('未读通知数量读取失败'), type: 'error' })
      } finally {
        this.typeCountsLoading = false
      }
    },
    async loadList() {
      this.listLoading = true
      this.listError = ''
      try {
        const result = await loadNotificationPage(this.currentType, this.page, this.pageSize)
        this.rows = result.rows
        this.total = result.total
      } catch (error) {
        this.rows = []
        this.total = 0
        this.listError = error.message || this.$t('通知列表读取失败')
      } finally {
        this.listLoading = false
      }
    },
    selectType(type) {
      if (this.currentType === type && this.page === 1) return
      this.currentType = type
      this.page = 1
      this.$router.replace({
        path: '/app/notifications',
        query: { type: String(type) }
      })
      this.loadList()
    },
    changePage(page) {
      if (page === this.page) return
      this.page = page
      this.loadList()
    },
    async readAll() {
      if (this.readAllLoading || this.totalUnread === 0) return
      this.readAllLoading = true
      try {
        await markAllNotificationsRead()
        this.typeCounts = this.types.reduce((counts, item) => {
          counts[item.id] = 0
          return counts
        }, {})
        this.rows = this.rows.map(item => ({ ...item, state: 2, unread: false }))
        this.$emit('unread-count', 0)
        this.$emit('toast', { message: this.$t('全部通知已标记为已读'), type: 'success' })
      } catch (error) {
        this.$emit('toast', { message: error.message || this.$t('全部已读操作失败'), type: 'error' })
      } finally {
        this.readAllLoading = false
      }
    },
    async loadDetail() {
      const id = String(this.messageId || '').trim()
      if (!id) return
      this.detailLoading = true
      this.detailError = ''
      this.detail = null
      try {
        this.detail = await loadNotificationDetail(id)
        this.$emit('read', id)
      } catch (error) {
        this.detailError = error.message || this.$t('通知详情读取失败')
      } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style scoped>
.notification-center {
  min-height: calc(100vh - 164px);
  padding: 28px 0 40px;
}

.notification-center__page-heading {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.notification-center__page-heading h1 {
  margin: 0;
  color: #101828;
  font-size: 25px;
  font-weight: 800;
}

.notification-center__page-heading p {
  margin: 6px 0 0;
  color: #8992a3;
  font-size: 13px;
}

.notification-center__read-all,
.notification-center__detail-heading button {
  min-height: 38px;
  padding: 0 15px;
  color: #344054;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 700;
}

.notification-center__read-all:hover:not(:disabled),
.notification-center__detail-heading button:hover {
  color: #1f6bff;
  border-color: #a9c4f7;
}

.notification-center__read-all:disabled {
  cursor: not-allowed;
  opacity: .5;
}

.notification-center__layout {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  align-items: flex-start;
  gap: 18px;
}

.notification-center__categories,
.notification-center__list,
.notification-detail {
  border: 1px solid #e4e8f0;
  border-radius: 13px;
  background: #fff;
}

.notification-center__categories {
  overflow: hidden;
  padding: 10px;
}

.notification-center__categories > button {
  display: flex;
  width: 100%;
  min-height: 52px;
  padding: 8px 10px;
  align-items: center;
  gap: 10px;
  color: #475467;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  text-align: left;
}

.notification-center__categories > button + button {
  margin-top: 4px;
}

.notification-center__categories > button:hover {
  background: #f7f9fc;
}

.notification-center__categories > button.active {
  color: #1f6bff;
  border-color: #dce8fd;
  background: #f0f5fe;
}

.notification-center__category-icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  place-items: center;
  color: #1f6bff;
  border-radius: 8px;
  background: #eaf1ff;
}

.notification-center__category-icon.type-2 {
  color: #b66a00;
  background: #fff3dd;
}

.notification-center__category-icon.type-6 {
  color: #6941c6;
  background: #f2ecff;
}

.notification-center__category-icon.type-1 {
  color: #067647;
  background: #e9f8ef;
}

.notification-center__categories strong {
  min-width: 0;
  flex: 1;
  font-size: 13px;
  font-weight: 700;
}

.notification-center__category-count {
  min-width: 20px;
  padding: 2px 5px;
  color: #fff;
  border-radius: 999px;
  background: #e5484d;
  font-size: 10px;
  font-style: normal;
  line-height: 16px;
  text-align: center;
}

.notification-center__category-arrow {
  color: #b0b7c3;
  font-size: 11px;
}

.notification-center__list {
  overflow: hidden;
  min-height: 520px;
}

.notification-center__list > header {
  display: flex;
  min-height: 70px;
  padding: 0 22px;
  align-items: center;
  border-bottom: 1px solid #eef0f5;
}

.notification-center__list > header h2 {
  margin: 0;
  color: #101828;
  font-size: 17px;
  font-weight: 800;
}

.notification-center__list > header span {
  display: block;
  margin-top: 4px;
  color: #98a0af;
  font-size: 11.5px;
}

.notification-center__rows {
  min-height: 390px;
}

.notification-center__row {
  display: grid;
  width: 100%;
  min-height: 78px;
  padding: 13px 22px;
  grid-template-columns: 8px minmax(0, 1fr) 150px 14px;
  align-items: center;
  gap: 12px;
  color: inherit;
  border: 0;
  border-bottom: 1px solid #f0f2f6;
  background: #fff;
  text-align: left;
}

.notification-center__row:hover {
  background: #f8faff;
}

.notification-center__row-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d0d5dd;
}

.notification-center__row-dot.unread {
  background: #e5484d;
  box-shadow: 0 0 0 3px #fff0ef;
}

.notification-center__row-copy {
  min-width: 0;
}

.notification-center__row-copy strong,
.notification-center__row-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-center__row-copy strong {
  color: #1d2939;
  font-size: 13.5px;
  font-weight: 700;
}

.notification-center__row-copy small {
  margin-top: 5px;
  color: #98a0af;
  font-size: 12px;
}

.notification-center__row time {
  color: #98a0af;
  font-size: 11.5px;
  text-align: right;
  white-space: nowrap;
}

.notification-center__row > i {
  color: #b0b7c3;
  font-size: 11px;
}

.notification-center__list > footer {
  display: flex;
  min-height: 66px;
  padding: 0 20px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.notification-center__list > footer > span {
  color: #98a0af;
  font-size: 12px;
}

.notification-center__state {
  display: flex;
  min-height: 390px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #98a0af;
  font-size: 13px;
}

.notification-center__state > i {
  color: #6b96f5;
  font-size: 23px;
}

.notification-center__state.is-error > i {
  color: #e5484d;
}

.notification-center__state button {
  padding: 7px 13px;
  color: #1f6bff;
  border: 1px solid #c9d9f7;
  border-radius: 7px;
  background: #fff;
  font-size: 12px;
}

.notification-center__detail-heading {
  align-items: center;
}

.notification-center__detail-heading button i {
  margin-right: 3px;
}

.notification-detail {
  min-height: 560px;
  padding: 34px 44px 48px;
}

.notification-detail > header {
  padding-bottom: 24px;
  border-bottom: 1px solid #eef0f5;
}

.notification-detail > header > span {
  display: inline-block;
  padding: 3px 9px;
  color: #1f6bff;
  border-radius: 999px;
  background: #edf3ff;
  font-size: 11px;
  font-weight: 700;
}

.notification-detail h1 {
  margin: 12px 0 0;
  color: #101828;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.4;
}

.notification-detail time {
  display: block;
  margin-top: 8px;
  color: #98a0af;
  font-size: 12px;
}

.notification-detail__content {
  padding-top: 28px;
  color: #344054;
  font-size: 14px;
  line-height: 1.9;
  overflow-wrap: anywhere;
}

.notification-detail__content ::v-deep img,
.notification-detail__content ::v-deep video {
  max-width: 100%;
  height: auto;
}

.notification-detail__content ::v-deep a {
  color: #1f6bff;
}

.notification-detail__iframe {
  width: 100%;
  min-height: 720px;
  margin-top: 22px;
  border: 0;
}

@media (max-width: 900px) {
  .notification-center__layout {
    grid-template-columns: 1fr;
  }

  .notification-center__categories {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .notification-center__categories > button + button {
    margin-top: 0;
  }

  .notification-center__row {
    grid-template-columns: 8px minmax(0, 1fr) 14px;
  }

  .notification-center__row time {
    display: none;
  }

  .notification-detail {
    padding: 26px 22px 36px;
  }
}
</style>
